import { useState } from "react";
import { Plus, Save, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { BiometricReferenceRange } from "@/lib/api_client";
import { useReferenceRanges, useCreateReferenceRange, useUpdateReferenceRange, useDeleteReferenceRange } from "@/hooks/api";
import { REFERENCE_TYPE_LABELS, GENDER_LABELS } from "@/lib/types";
import type { ReferenceType } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";

interface ReferencesTabProps {
  biometricId: string;
}

interface EditableRow {
  id: string;
  referenceType: ReferenceType;
  gender: string;
  minAge: string;
  maxAge: string;
  value: string;
  isNew: boolean;
  dirty: boolean;
}

export function ReferencesTab({ biometricId }: ReferencesTabProps) {
  const { data: allRanges, isLoading } = useReferenceRanges();
  const createRange = useCreateReferenceRange();
  const updateRange = useUpdateReferenceRange();
  const deleteRange = useDeleteReferenceRange();

  const ranges = (allRanges ?? []).filter((r) => r.biometricId === biometricId);

  const [editRows, setEditRows] = useState<EditableRow[] | null>(null);

  // Initialize rows from fetched data once loaded
  const rows = editRows ?? ranges.map((r) => ({
    id: r.id,
    referenceType: r.referenceType as ReferenceType,
    gender: r.gender ?? "",
    minAge: String(r.minAge),
    maxAge: String(r.maxAge),
    value: String(r.value),
    isNew: false,
    dirty: false,
  }));

  const handleAdd = () => {
    const newRows = [...rows, {
      id: `new-${Date.now()}`,
      referenceType: "normal_min" as ReferenceType,
      gender: "",
      minAge: "18",
      maxAge: "120",
      value: "0",
      isNew: true,
      dirty: true,
    }];
    setEditRows(newRows);
  };

  const handleChange = (idx: number, field: keyof EditableRow, value: string) => {
    const newRows = rows.map((r, i) =>
      i === idx ? { ...r, [field]: value, dirty: true } : r
    );
    setEditRows(newRows);
  };

  const handleSaveRow = async (idx: number) => {
    const row = rows[idx]!;
    const data = {
      biometricId,
      referenceType: row.referenceType,
      gender: row.gender || null,
      minAge: row.minAge,
      maxAge: row.maxAge,
      value: row.value,
    };

    if (row.isNew) {
      const created = await createRange.mutateAsync(data);
      const newRows = rows.map((r, i) =>
        i === idx ? { ...r, id: created.id, isNew: false, dirty: false } : r
      );
      setEditRows(newRows);
    } else {
      await updateRange.mutateAsync({ id: row.id, data });
      const newRows = rows.map((r, i) => (i === idx ? { ...r, dirty: false } : r));
      setEditRows(newRows);
    }
    toast.success("Referanse lagret");
  };

  const handleDeleteRow = async (idx: number) => {
    const row = rows[idx]!;
    if (!row.isNew) {
      await deleteRange.mutateAsync(row.id);
    }
    const newRows = rows.filter((_, i) => i !== idx);
    setEditRows(newRows);
    toast.success("Referanse slettet");
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-admin-text-subtle" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-admin-text-secondary">Referanseomrader</h3>
        <Button variant="outline" size="sm" onClick={handleAdd}>
          <Plus className="h-3 w-3" />
          Legg til
        </Button>
      </div>

      <div className="rounded-lg border border-admin-card-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-admin-card">
              <TableHead>Type</TableHead>
              <TableHead>Kjonn</TableHead>
              <TableHead>Alder min</TableHead>
              <TableHead>Alder max</TableHead>
              <TableHead>Verdi</TableHead>
              <TableHead className="w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Select
                    value={row.referenceType}
                    onChange={(e) => handleChange(idx, "referenceType", e.target.value)}
                    className="w-36"
                  >
                    {(Object.keys(REFERENCE_TYPE_LABELS) as ReferenceType[]).map((type) => (
                      <option key={type} value={type}>
                        {REFERENCE_TYPE_LABELS[type]}
                      </option>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  <Select
                    value={row.gender}
                    onChange={(e) => handleChange(idx, "gender", e.target.value)}
                    className="w-28"
                  >
                    <option value="">Alle</option>
                    {Object.entries(GENDER_LABELS).map(([val, label]) => (
                      <option key={val} value={val}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    value={row.minAge}
                    onChange={(e) => handleChange(idx, "minAge", e.target.value)}
                    className="w-20"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    value={row.maxAge}
                    onChange={(e) => handleChange(idx, "maxAge", e.target.value)}
                    className="w-20"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    step="any"
                    value={row.value}
                    onChange={(e) => handleChange(idx, "value", e.target.value)}
                    className="w-24"
                  />
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleSaveRow(idx)}
                      disabled={!row.dirty}
                      title="Lagre"
                    >
                      <Save className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteRow(idx)}
                      className="text-admin-negative hover:text-admin-negative"
                      title="Slett"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-admin-text-subtle py-8">
                  Ingen referanseomrader lagt til
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
